// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useLocalization, type Language } from '../../config/localization';
import BaseWidget from './BaseWidget';
import HybridDropdown from '../ui/dropdowns/HybridDropdown';
import Tooltip from '../ui/Tooltip';
import Icon, { ICONS } from '../ui/icons';
import type { GalleryFilters } from '../pages/gallery/GalleryContent';

interface Collection {
  id: number;
  name: string;
  description?: string;
  image_count?: number;
}

interface User {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface Workflow {
  id: number;
  name: string;
  visual_id: string;
}

interface GalleryFiltersWidgetProps {
  language: Language;
  filters: GalleryFilters;
  onFiltersChange: (filters: Partial<GalleryFilters>) => void;
  organizationId?: number;
  className?: string;
  widgetId?: string;
}

export default function GalleryFiltersWidget({
  language,
  filters,
  onFiltersChange,
  organizationId,
  className = '',
  widgetId
}: GalleryFiltersWidgetProps) {
  const { getToken } = useAuth();
  const { currentOrganization } = useOrganization();
  const t = useLocalization(language);

  // Filter data state
  const [collections, setCollections] = useState<Collection[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [loading, setLoading] = useState(false);

  // Local filter state
  const [localFilters, setLocalFilters] = useState<GalleryFilters>(filters);

  // Update local filters when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Fetch filter options
  useEffect(() => {
    const orgId = organizationId || currentOrganization?.id;
    if (orgId) {
      fetchFilterOptions(orgId);
    }
  }, [organizationId, currentOrganization?.id]);

  const fetchFilterOptions = async (orgId: number) => {
    setLoading(true);
    try {
      const token = await getToken({ template: 'withmail' });
      
      // Fetch collections, users, and workflows in parallel
      const [collectionsRes, usersRes, workflowsRes] = await Promise.all([
        fetch(`/api/organization/collections?organizationId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/organization/users?organizationId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/organization/workflows?organizationId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (collectionsRes.ok) {
        const collectionsData = await collectionsRes.json();
        setCollections(collectionsData.collections || []);
      }

      if (usersRes.ok) {
        const usersData = await usersRes.json();
        setUsers(usersData.users || []);
      }

      if (workflowsRes.ok) {
        const workflowsData = await workflowsRes.json();
        setWorkflows(workflowsData.workflows || []);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLocalFilterChange = (key: keyof GalleryFilters, value: any) => {
    console.log(`🔧 handleLocalFilterChange called:`, {
      key,
      value,
      valueType: typeof value,
      isArray: Array.isArray(value),
      arrayLength: Array.isArray(value) ? value.length : 'N/A'
    });
    
    const updatedFilters = { ...localFilters, [key]: value };
    console.log('📝 Updated filters will be:', updatedFilters);
    
    setLocalFilters(updatedFilters);
    // Pass the complete updated filters object, not just the changed field
    onFiltersChange(updatedFilters);
  };


  return (
    <BaseWidget
      title={t.widgets?.galleryFilters || 'Gallery Filters'}
      icon={<Icon name={ICONS.SETTINGS} size={16} className="text-current" />}
      description={t.widgets?.galleryFiltersDesc || 'Filter and search gallery content'}
      language={language}
      className={className}
      widgetId={widgetId}
    >
      <div className="space-y-4">
        {/* Date Range */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-300">
              {t.galleryPage?.dateRange || 'Date Range'}
            </label>
            <Tooltip content={t.widgets?.dateRangeTooltip || 'Filter images by creation date range'}>
              <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
                <span className="text-xs text-gray-300 font-medium">?</span>
              </div>
            </Tooltip>
          </div>
          <div className="space-y-2">
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {t.galleryPage?.dateFrom || 'From'}
              </label>
              <input
                type="date"
                value={localFilters.dateFrom || ''}
                onChange={(e) => handleLocalFilterChange('dateFrom', e.target.value || undefined)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-sm [color-scheme:dark]"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">
                {t.galleryPage?.dateTo || 'To'}
              </label>
              <input
                type="date"
                value={localFilters.dateTo || ''}
                onChange={(e) => handleLocalFilterChange('dateTo', e.target.value || undefined)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-sm [color-scheme:dark]"
              />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700/50"></div>

        {/* Content Type Filter */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-300">
              {t.galleryPage?.contentType || 'Content Type'}
            </label>
            <Tooltip content={t.widgets?.contentTypeTooltip || 'Filter by media type (images or videos)'}>
              <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
                <span className="text-xs text-gray-300 font-medium">?</span>
              </div>
            </Tooltip>
          </div>
          <HybridDropdown
            options={[
              {
                id: 'image',
                label: t.galleryPage?.images || 'Images',
                icon: ICONS.IMAGE
              },
              {
                id: 'video',
                label: t.galleryPage?.videos || 'Videos',
                icon: ICONS.VIDEO
              },
              {
                id: 'audio',
                label: t.galleryPage?.audio || 'Audio',
                icon: ICONS.AUDIO
              },
              {
                id: '3d',
                label: t.galleryPage?.threeD || '3D',
                icon: ICONS.CUBE_3D
              },
              {
                id: 'text',
                label: t.galleryPage?.textType || 'Text',
                icon: ICONS.TEXT
              }
            ]}
            selectedIds={localFilters.contentTypes || []}
            onSelectionChange={(ids) => handleLocalFilterChange('contentTypes', ids.length > 0 ? ids as string[] : undefined)}
            placeholder={t.galleryPage?.selectContentTypes || 'Select content types...'}
            searchPlaceholder={t.galleryPage?.searchContentTypes || 'Search content types...'}
            selectedLabel={t.widgets?.selectedContentTypes || 'Selected content types'}
            loading={false}
            language={language}
            emptyMessage={t.galleryPage?.noContentTypesFound || 'No content types found'}
            allowMultiple={true}
          />
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700/50"></div>

        {/* Approve State Filter (like / dislike / approved mark) */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-300">
              {t.galleryPage?.approveState || 'Approve State'}
            </label>
            <Tooltip content={t.widgets?.approveStateTooltip || 'Filter by like / dislike / approved mark'}>
              <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
                <span className="text-xs text-gray-300 font-medium">?</span>
              </div>
            </Tooltip>
          </div>
          <HybridDropdown
            options={[
              {
                id: 'like',
                label: t.gallery?.mark?.like || 'Like',
                icon: ICONS.THUMBS_UP
              },
              {
                id: 'dislike',
                label: t.gallery?.mark?.dislike || 'Dislike',
                icon: ICONS.THUMBS_DOWN
              },
              {
                id: 'approved',
                label: t.gallery?.mark?.approved || 'Approved',
                icon: ICONS.CHECK
              }
            ]}
            selectedIds={localFilters.marks || []}
            onSelectionChange={(ids) => handleLocalFilterChange('marks', ids.length > 0 ? ids as string[] : undefined)}
            placeholder={t.galleryPage?.selectApproveState || 'Select approve state...'}
            searchPlaceholder={t.galleryPage?.searchApproveState || 'Search approve state...'}
            selectedLabel={t.widgets?.selectedApproveState || 'Selected approve state'}
            loading={false}
            language={language}
            emptyMessage={t.galleryPage?.noApproveStateFound || 'No approve state found'}
            allowMultiple={true}
          />
        </div>

        {/* Divider */}
        <div className="border-t border-gray-700/50"></div>

        {/* Collections */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-300">
              {t.galleryPage?.collections || 'Collections'}
            </label>
            <Tooltip content={t.widgets?.collectionsTooltip || 'Filter by collections containing the images'}>
              <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
                <span className="text-xs text-gray-300 font-medium">?</span>
              </div>
            </Tooltip>
          </div>
          <HybridDropdown
            options={collections.map(collection => ({
              id: collection.id,
              label: collection.name,
              subtitle: collection.description,
              count: collection.image_count
            }))}
            selectedIds={localFilters.collectionIds || []}
            onSelectionChange={(ids) => handleLocalFilterChange('collectionIds', ids.length > 0 ? ids as number[] : undefined)}
            placeholder={t.galleryPage?.selectCollections || 'Select collections...'}
            searchPlaceholder={t.galleryPage?.searchCollections || 'Search collections...'}
            selectedLabel={t.widgets?.selectedCollections || 'Выбранные коллекции'}
            loading={loading}
            language={language}
            emptyMessage={t.collections?.noCollections || 'No collections found'}
            allowMultiple={true}
          />
        </div>

        {/* Divider */}
        {workflows.length > 0 && <div className="border-t border-gray-700/50"></div>}

        {/* Workflows */}
        {workflows.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-300">
                {t.galleryPage?.workflows || 'Workflows'}
              </label>
              <Tooltip content={t.widgets?.workflowsTooltip || 'Filter by workflow used to generate images'}>
                <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
                  <span className="text-xs text-gray-300 font-medium">?</span>
                </div>
              </Tooltip>
            </div>
            <HybridDropdown
              options={workflows.map(workflow => ({
                id: workflow.id,
                label: workflow.name,
                subtitle: workflow.visual_id
              }))}
              selectedIds={localFilters.workflowIds || []}
              onSelectionChange={(ids) => handleLocalFilterChange('workflowIds', ids.length > 0 ? ids as number[] : undefined)}
              placeholder={t.galleryPage?.selectWorkflows || 'Select workflows...'}
              searchPlaceholder={t.galleryPage?.searchWorkflows || 'Search workflows...'}
              selectedLabel={t.widgets?.selectedWorkflows || 'Выбранные процессы'}
              loading={loading}
              language={language}
              emptyMessage={t.galleryPage?.noWorkflowsFound || 'No workflows found'}
              allowMultiple={true}
            />
          </div>
        )}

        {/* Divider */}
        {users.length > 0 && <div className="border-t border-gray-700/50"></div>}

        {/* Users */}
        {users.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-gray-300">
                {t.galleryPage?.users || 'Users'}
              </label>
              <Tooltip content={t.widgets?.usersTooltip || 'Filter by users who created the images'}>
                <div className="w-4 h-4 bg-gray-700/50 border border-gray-600 rounded-full flex items-center justify-center cursor-help hover:bg-gray-600/50 transition-colors duration-200">
                  <span className="text-xs text-gray-300 font-medium">?</span>
                </div>
              </Tooltip>
            </div>
            <HybridDropdown
              options={users.map(user => ({
                id: user.user_id,
                label: user.email,
                subtitle: [user.first_name, user.last_name].filter(Boolean).join(' ') || undefined
              }))}
              selectedIds={localFilters.userIds || []}
              onSelectionChange={(ids) => handleLocalFilterChange('userIds', ids.length > 0 ? ids as string[] : undefined)}
              placeholder={t.galleryPage?.selectUsers || 'Select users...'}
              searchPlaceholder={t.galleryPage?.searchUsers || 'Search users...'}
              selectedLabel={t.widgets?.selectedUsers || 'Выбранные пользователи'}
              loading={loading}
              language={language}
              emptyMessage={t.galleryPage?.noUsersFound || 'No users found'}
              allowMultiple={true}
            />
          </div>
        )}
      </div>
    </BaseWidget>
  );
}